import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { convertToPredictedAndPublicRestaurantList } from '../helpers/converters'
import okaoAxios, { okaoAxiosNoAuth } from '../helpers/axios'
import {
  PredictedRestaurantRequest,
  Restaurant,
  RestaurantAndPredictedRestaurant,
} from '../types'
import { RootState } from './'

export interface FoodArea {
  foodAreaId: string
  city: string
  name: string
  description: string
  centerLat: number
  centerLng: number
  radiusMeters: number
  tagType: string
  score: number
  restaurantCount: number
}

interface FoodAreaState {
  foodAreas: FoodArea[]
  foodAreaRestaurants: RestaurantAndPredictedRestaurant[]
  loading: boolean
  error: string | undefined
}

const initialState: FoodAreaState = {
  foodAreas: [],
  foodAreaRestaurants: [],
  loading: false,
  error: undefined,
}

export const getFoodAreasByCity = createAsyncThunk(
  'foodArea/getFoodAreasByCity',
  async (city: string) => {
    const response = await okaoAxiosNoAuth().get(`/food-areas?city=${city}`)
    return response?.data as FoodArea[]
  },
)

export const getFoodAreasByLocation = createAsyncThunk(
  'foodArea/getFoodAreasByLocation',
  async (params: { lat: number; lng: number }) => {
    const response = await okaoAxiosNoAuth().get(
      `/food-areas?lat=${params.lat}&lng=${params.lng}`,
    )
    return response?.data as FoodArea[]
  },
)

const haversineDistanceMiles = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const R = 3958.8
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export const getFoodAreaRestaurants = createAsyncThunk(
  'foodArea/getFoodAreaRestaurants',
  async (params: { foodAreaId: string; latitude?: number; longitude?: number }) => {
    const response = await okaoAxiosNoAuth().get(
      `/food-areas/${params.foodAreaId}/restaurants`,
    )
    const restaurants = (response?.data ?? []) as Restaurant[]
    const { latitude, longitude } = params
    if (latitude != null && longitude != null) {
      restaurants.forEach((r) => {
        if (r.lat != null && r.lng != null) {
          r.distance = Math.round(haversineDistanceMiles(latitude, longitude, r.lat, r.lng) * 10) / 10
        }
      })
    }
    return convertToPredictedAndPublicRestaurantList(restaurants)
  },
)

export const enrichFoodAreaRestaurantsWithPredictions = createAsyncThunk(
  'foodArea/enrichFoodAreaRestaurantsWithPredictions',
  async (payload: PredictedRestaurantRequest) => {
    const response = await okaoAxios.post(`/restaurants/predict`, payload)
    return response?.data as { count: number; result: RestaurantAndPredictedRestaurant[] }
  },
)

export const foodAreaSlice = createSlice({
  name: 'foodArea',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getFoodAreasByCity.pending, (state) => {
        state.loading = true
        state.error = undefined
      })
      .addCase(getFoodAreasByCity.fulfilled, (state, action) => {
        state.loading = false
        state.foodAreas = action.payload ?? []
      })
      .addCase(getFoodAreasByCity.rejected, (state) => {
        state.loading = false
        state.foodAreas = []
      })
      .addCase(getFoodAreasByLocation.pending, (state) => {
        state.loading = true
        state.error = undefined
      })
      .addCase(getFoodAreasByLocation.fulfilled, (state, action) => {
        state.loading = false
        state.foodAreas = action.payload ?? []
      })
      .addCase(getFoodAreasByLocation.rejected, (state) => {
        state.loading = false
        state.foodAreas = []
      })
      .addCase(getFoodAreaRestaurants.pending, (state) => {
        state.loading = true
        state.error = undefined
        state.foodAreaRestaurants = []
      })
      .addCase(getFoodAreaRestaurants.fulfilled, (state, action) => {
        state.loading = false
        state.foodAreaRestaurants = action.payload ?? []
      })
      .addCase(getFoodAreaRestaurants.rejected, (state) => {
        state.loading = false
        state.foodAreaRestaurants = []
      })
      // Silent enrichment — does not touch loading state
      .addCase(
        enrichFoodAreaRestaurantsWithPredictions.fulfilled,
        (state, action) => {
          if (action.payload.count > 0) {
            // Preserve client-computed distances from the base fetch
            const distanceByAlias = new Map(
              state.foodAreaRestaurants.map((r) => [
                r.restaurantDTO?.alias,
                r.restaurantDTO?.distance,
              ]),
            )
            state.foodAreaRestaurants = action.payload.result.map((r) => {
              const prev = distanceByAlias.get(r.restaurantDTO?.alias)
              if (!r.restaurantDTO || r.restaurantDTO.distance != null || prev == null) {
                return r
              }
              return { ...r, restaurantDTO: { ...r.restaurantDTO, distance: prev } }
            })
          }
        },
      )
  },
})

export const selectFoodAreas = (state: RootState): FoodArea[] =>
  state.foodArea.foodAreas

export const selectFoodAreaRestaurants = (
  state: RootState,
): RestaurantAndPredictedRestaurant[] => state.foodArea.foodAreaRestaurants

export const selectFoodAreasLoading = (state: RootState): boolean =>
  state.foodArea.loading

export default foodAreaSlice.reducer
